import React, { JSX, useEffect, useMemo, useRef, useState } from 'react';
import { MapContainer } from 'react-leaflet';
import LeafletMapComponent from './LeafletMapComponent';
import { LatLngBounds, LatLngExpression } from 'leaflet';
import { MapConfig, Marker } from './MapsInterfaces';
import Keys from '../../Translations/generated/park-map.en.json.keys';
import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faClose } from '@fortawesome/free-solid-svg-icons';
import { useMount } from 'react-use';
import CmsGlobalElements from '../../Models/CMS/CmsGlobalElements';
import useGlobalElementsService from '../../Hooks/useGlobalElementsService';

interface Props {
  mapConfig: MapConfig;
  handleHotelClick?: (hotelId: string) => Promise<void>;
  isModalOpen: boolean;
  handleCloseModal: (e?: React.MouseEvent<HTMLButtonElement>) => void;
}

const MapModal = (props: Props): JSX.Element => {
  const { mapConfig, handleHotelClick, isModalOpen, handleCloseModal } = props;

  const dialogRef = useRef<HTMLDialogElement>(null);
  const innerMapRef = useRef<(L.Map & { toggleFullscreen: () => void }) | null>(null);
  const mapObjectRef = useRef<
    (L.Map & { toggleFullscreen: () => void; isFullscreen: () => void }) | null
  >(null);

  const { t } = useTranslation('park-map');

  const cmsGlobalElementsService = useGlobalElementsService();
  const [useNewMap, setUseNewMap] = useState(false);
  const [mapCenterRadius, setMapCenterRadius] = useState<number | undefined>();
  const [mapReady, setMapReady] = useState(false);

  useEffect(() => {
    return () => {
      if (mapObjectRef.current) {
        mapObjectRef.current.remove();
        mapObjectRef.current = null;
      }
    };
  }, []);

  useMount(() => {
    cmsGlobalElementsService.fetchGlobalElements().then((ge: CmsGlobalElements) => {
      if (ge) {
        if (ge.showNewMap) {
          setUseNewMap(ge.showNewMap);
          setMapCenterRadius(ge.centerMapRadius);
        }
      }
    });
  });

  const isNewMapCondition = (marker: Marker) => {
    if (useNewMap) {
      return (
        marker.isPark ||
        (!marker.isPark && (marker?.distance?.floatDistance === 0 || !marker?.distance))
      );
    } else {
      return marker?.distance?.floatDistance === 0 || !marker?.distance;
    }
  };

  const bounds: LatLngBounds = useMemo(() => {
    const filteredMarkers: LatLngExpression[] = (mapConfig.markers || [])
      .filter(marker => !!marker.geoData && isNewMapCondition(marker))
      .map(marker => [marker?.geoData?.latitude || 0, marker?.geoData?.longitude || 0]);

    const latLngBounds = new LatLngBounds(filteredMarkers);

    if (mapConfig.pad) {
      return latLngBounds.pad(mapConfig.pad);
    }

    return latLngBounds;
  }, [useNewMap, mapConfig.markers]);

  useEffect(() => {
    document.body.style.overflow = isModalOpen ? 'hidden' : 'unset';

    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isModalOpen]);

  useEffect(() => {
    const dialog = dialogRef.current;
    if (!dialog) return;

    if (isModalOpen) {
      dialog.showModal();
      const id = requestAnimationFrame(() => setMapReady(true));
      return () => cancelAnimationFrame(id);
    }
    dialog.close();
    setMapReady(false);
  }, [isModalOpen]);

  useEffect(() => {
    const dialog = dialogRef.current;
    if (!dialog) return;
    const onClose = () => handleCloseModal();
    dialog.addEventListener('close', onClose);
    return () => dialog.removeEventListener('close', onClose);
  }, [handleCloseModal]);

  return (
    <dialog className="map-modal" ref={dialogRef} aria-label={t(Keys.ariaMapLabel)}>
      <button className="close-button" onClick={handleCloseModal}>
        {t(Keys.close)} <FontAwesomeIcon icon={faClose} />
      </button>

      {mapReady && (
        <MapContainer
          key={mapConfig.mapKey}
          ref={mapObjectRef}
          bounds={bounds.isValid() ? bounds : undefined}
          keyboard={true}
        >
          <LeafletMapComponent
            markers={mapConfig.markers}
            bounds={bounds}
            centerMap={mapConfig.centerMap}
            centerMapRadius={mapCenterRadius}
            containerRef={dialogRef}
            innerMapRef={innerMapRef}
            handleHotelClick={handleHotelClick}
          />
        </MapContainer>
      )}
    </dialog>
  );
};

export default MapModal;
